import { type ElementRef, type TextareaHTMLAttributes, forwardRef } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { cn } from '../lib/utils';

const textareaVariants = tv({
  slots: {
    root: 'flex flex-col gap-1',
    label: 'text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300',
    textarea: [
      'w-full resize-y rounded-md border border-uniform-gray-300 bg-white px-3 py-2 text-sm',
      'text-uniform-gray-900 placeholder:text-uniform-gray-400',
      'dark:border-uniform-gray-600 dark:bg-uniform-gray-800 dark:text-uniform-gray-100',
      'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
      'disabled:cursor-not-allowed disabled:bg-uniform-gray-50 disabled:text-uniform-gray-400'
    ],
    help: 'text-xs text-uniform-gray-600 dark:text-uniform-gray-400'
  }
});

type TextareaVariants = VariantProps<typeof textareaVariants>;

interface TextareaProps extends TextareaHTMLAttributes<ElementRef<'textarea'>> {
  label?: string;
  labelText?: string;
  helpText?: string;
}

const Textarea = forwardRef<ElementRef<'textarea'>, TextareaProps>(
  ({ label, labelText, helpText, id, className, ...props }, ref) => {
    const displayLabel = label ?? labelText;
    const vars = textareaVariants();
    return (
      <div className={cn(vars.root(), className)}>
        {displayLabel && (
          <label htmlFor={id} className={vars.label()}>
            {displayLabel}
          </label>
        )}
        <textarea ref={ref} id={id} className={vars.textarea()} {...props} />
        {helpText && <p className={vars.help()}>{helpText}</p>}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea, textareaVariants };
export type { TextareaProps, TextareaVariants };
